// src/pages/Leaderboard.js
import React, { useState, useEffect } from 'react';
import { useTheme, TOKENS } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const MEDALS = ['🥇', '🥈', '🥉'];

export default function Leaderboard() {
  const { theme } = useTheme();
  const c = TOKENS[theme];
  const { user } = useAuth();

  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res  = await fetch(`${API}/leaderboard/global`);
        const data = await res.json();
        // top 10 only
        setLeaders(Array.isArray(data) ? data.slice(0, 10) : []);
      } catch (e) {
        setError('Could not load leaderboard');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <>
      <SEO title="Leaderboard — TrailCode" description="Top Salesforce developers on TrailCode ranked by challenge points." />
      <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', sans-serif", transition: 'background 0.25s' }}>
        <Navbar />

        <div style={{ maxWidth: 640, margin: '0 auto', padding: '48px 24px 80px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h1 style={{ fontSize: 32, fontWeight: 700, color: c.text, margin: '0 0 8px', letterSpacing: '-0.5px' }}>🏆 Leaderboard</h1>
            <p style={{ fontSize: 14, color: c.secondary, margin: 0 }}>Top 10 Salesforce developers by challenge points</p>
          </div>

          {loading ? (
            // Skeleton
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...Array(10)].map((_, i) => (
                <div key={i} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, height: 64, opacity: 1 - i * 0.07 }} />
              ))}
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40, color: c.secondary }}>{error}</div>
          ) : leaders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <p style={{ fontSize: 32, marginBottom: 12 }}>🚀</p>
              <p style={{ fontSize: 16, fontWeight: 600, color: c.text, marginBottom: 8 }}>No entries yet</p>
              <p style={{ fontSize: 13, color: c.secondary }}>Be the first on the leaderboard — solve a challenge!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {leaders.map((entry, i) => {
                const isMe = user && entry.user_id === user.id;
                const name = entry.users?.name || 'Anonymous';
                const avatar = entry.users?.avatar_url;
                const points = entry.total_points || 0;
                const streak = entry.current_streak || 0;
                const rank = i + 1;

                return (
                  <div key={entry.user_id} style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    background: isMe ? c.accentBg : c.card,
                    border: `1px solid ${isMe ? c.accent : c.border}`,
                    borderRadius: 12, padding: '14px 20px',
                    transition: 'border-color 0.15s',
                  }}>
                    {/* Rank */}
                    <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                      {rank <= 3
                        ? <span style={{ fontSize: 22 }}>{MEDALS[rank - 1]}</span>
                        : <span style={{ fontSize: 15, fontWeight: 700, color: c.muted }}>#{rank}</span>
                      }
                    </div>

                    {/* Avatar */}
                    {avatar
                      ? <img src={avatar} alt={name} style={{ width: 36, height: 36, borderRadius: '50%', border: `1.5px solid ${c.border}`, flexShrink: 0 }} />
                      : <div style={{ width: 36, height: 36, borderRadius: '50%', background: c.accentBg, border: `1.5px solid ${c.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: c.accent, flexShrink: 0 }}>{name[0]?.toUpperCase()}</div>
                    }

                    {/* Name */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: isMe ? 700 : 500, color: c.text }}>
                        {name} {isMe && <span style={{ fontSize: 11, color: c.accent }}>(you)</span>}
                      </div>
                      {streak > 0 && <div style={{ fontSize: 11, color: c.muted, marginTop: 2 }}>🔥 {streak} day streak</div>}
                    </div>

                    {/* Points */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: rank <= 3 ? c.accent : c.text }}>{points.toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: c.muted }}>pts</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer note */}
          <p style={{ textAlign: 'center', fontSize: 12, color: c.muted, marginTop: 32 }}>
            Points earned from challenge submissions · Updates in real time
          </p>
        </div>
      </div>
    </>
  );
}